import { Link, useLocation } from '@tanstack/react-router';
import { ArrowRight, CalendarClock, ChevronDown, Send } from 'lucide-react';
import React, {
    type ReactElement,
    type ReactNode,
    useEffect,
    useId,
    useMemo,
    useState,
} from 'react';

import { ShiftExcludedCaregiversSummary } from '@apps/web/components/outreaches/inhouse_recommendations_table';
import {
    type AssistantPromptAction,
    DEFAULT_SHIFT_ACTION_POLICY,
    type ReferenceData,
    type ShiftActionPolicy,
    type ShiftTab,
} from '@apps/web/components/phoebe_chat/assistant_response_types';
import { usePhoebeChatOrganizationScope } from '@apps/web/components/phoebe_chat/chat_organization_scope';
import { useIsExtensionSurface } from '@apps/web/components/phoebe_chat/extension_surface_context';
import {
    buildSlackMentionMap,
    type SlackUserMentionData,
} from '@apps/web/components/phoebe_chat/slack_mentions';
import { AssistantResponseDestinationLinks } from '@apps/web/components/phoebe_chat_view/assistant_responses/assistant_response_destination_links';
import {
    processContentForMarkdown,
    renderReferenceAwareAnchor,
    renderReferenceBadgeLink,
} from '@apps/web/components/phoebe_chat_view/assistant_responses/reference_markdown';
import {
    formatDetailStatus,
    TOOL_SURFACE_HOVER_CLASS,
    ToolToken,
    ToolTopicSection,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { type MessageDestinationLink } from '@apps/web/components/phoebe_chat_view/transcript_types';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Markdown } from '@apps/web/components/ui/markdown';
import { cn } from '@apps/web/lib/utils';

type AssistantCaregiverSuggestion = {
    readonly rank: number;
    readonly caregiverName: string;
    readonly reason: string;
    readonly reference: ReferenceData | null;
    readonly roleLabel: string | null;
};

type AssistantCaregiverSuggestionResponse = {
    readonly intro: string;
    readonly outro: string;
    readonly suggestions: Array<AssistantCaregiverSuggestion>;
};
type SuggestionAccordionTimeRow = {
    readonly label: string;
    readonly selectedCount: number;
};

function getReferenceMetadataString(
    reference: ReferenceData,
    key: string,
): string | null {
    const value = reference.metadata?.[key];
    return typeof value === 'string' && value.trim().length > 0
        ? value.trim()
        : null;
}

function getAssistantResponseCalloutReferences(
    references: ReadonlyArray<ReferenceData> | undefined,
): Array<ReferenceData> {
    const seenCalloutIds = new Set<string>();
    const callouts: Array<ReferenceData> = [];
    for (const reference of references ?? []) {
        const normalizedId = reference.id.toLowerCase();
        if (reference.type !== 'callout' || seenCalloutIds.has(normalizedId)) {
            continue;
        }
        seenCalloutIds.add(normalizedId);
        callouts.push(reference);
    }
    return callouts;
}

// Persistent navigation chips for outreaches referenced by the reply
// (e.g. just-created drafts). References are stored with the message, so
// the chips survive reopening the conversation.
function AssistantResponseCalloutLinks({
    references,
}: {
    readonly references?: Array<ReferenceData>;
}) {
    const { pathname } = useLocation();
    // In the extension this is the one deliberate escape hatch to the full app:
    // "Open in web" opens the outreach in a new browser tab (an absolute URL so
    // the in-extension link guard leaves it alone). In the web app it stays an
    // in-app "Go to outreach" link.
    const isExtensionSurface = useIsExtensionSurface();
    const callouts = useMemo(
        () =>
            // No chip for the outreach whose page is already open (e.g.
            // the analysis chat docked on the outreach detail page).
            getAssistantResponseCalloutReferences(references).filter(
                (callout) =>
                    !pathname.toLowerCase().includes(callout.id.toLowerCase()),
            ),
        [references, pathname],
    );
    if (callouts.length === 0) {
        return null;
    }

    const linkClassName =
        'inline-flex h-7 items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2 text-xs font-medium text-blue-800 hover:bg-blue-100 hover:text-blue-900 dark:border-blue-900 dark:bg-blue-950/50 dark:text-blue-300 dark:hover:bg-blue-900/40 dark:hover:text-blue-200';

    return (
        <div className="mt-2 flex flex-wrap gap-2">
            {callouts.map((callout) => {
                const label = (
                    <React.Fragment>
                        {isExtensionSurface ? 'Open in web' : 'Go to outreach'}
                        {callouts.length > 1
                            ? ` · ${callout.display_name}`
                            : ''}
                        <ArrowRight className="size-3" />
                    </React.Fragment>
                );
                return isExtensionSurface ? (
                    <a
                        key={callout.id}
                        href={new URL(
                            `/outreaches/${callout.id}`,
                            window.location.origin,
                        ).toString()}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`assistant-response-callout-link-${callout.id}`}
                        className={linkClassName}
                    >
                        {label}
                    </a>
                ) : (
                    <Link
                        key={callout.id}
                        to="/outreaches/$callout-id"
                        params={{ 'callout-id': callout.id }}
                        data-testid={`assistant-response-callout-link-${callout.id}`}
                        className={linkClassName}
                    >
                        {label}
                    </Link>
                );
            })}
        </div>
    );
}

const MAX_ASSISTANT_SHIFT_CARDS = 3;

function getAssistantResponseShiftReferences(
    references: ReadonlyArray<ReferenceData> | undefined,
): Array<ReferenceData> {
    const seenShiftIds = new Set<string>();
    const shifts: Array<ReferenceData> = [];
    for (const reference of references ?? []) {
        const normalizedId = reference.id.toLowerCase();
        if (reference.type !== 'shift' || seenShiftIds.has(normalizedId)) {
            continue;
        }
        seenShiftIds.add(normalizedId);
        shifts.push(reference);
    }
    return shifts;
}

function formatAssistantShiftDate(
    iso: string | null,
    timezone: string,
): string | null {
    if (!iso) {
        return null;
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: timezone,
    }).format(date);
}

function formatAssistantShiftTimeRange({
    startIso,
    endIso,
    timezone,
}: {
    readonly startIso: string | null;
    readonly endIso: string | null;
    readonly timezone: string;
}): string | null {
    if (!startIso || !endIso) {
        return null;
    }
    const start = new Date(startIso);
    const end = new Date(endIso);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return null;
    }
    const formatter = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: timezone,
    });
    return `${formatter.format(start)} - ${formatter.format(end)}`;
}

function getAssistantShiftDisplayName(reference: ReferenceData): string {
    return (
        getReferenceMetadataString(reference, 'patient_name') ??
        reference.display_name
    );
}

function getAssistantShiftStatus(reference: ReferenceData): string | null {
    const status = getReferenceMetadataString(reference, 'status');
    if (!status) {
        return null;
    }
    return status.toLowerCase() === 'open'
        ? 'Open'
        : (formatDetailStatus(status) ?? status);
}

function getAssistantShiftStatusTone(
    reference: ReferenceData,
): 'blue' | 'green' | 'neutral' | 'red' {
    const status = getReferenceMetadataString(reference, 'status');
    const normalized = status?.toLowerCase() ?? '';
    if (normalized.includes('coverage') || normalized === 'open') {
        return 'red';
    }
    if (normalized === 'assigned' || normalized === 'filled') {
        return 'green';
    }
    return 'neutral';
}

function AssistantResponseShiftCards({
    references,
    onAssistantPromptAction,
    shiftActionPolicy = DEFAULT_SHIFT_ACTION_POLICY,
}: {
    readonly references?: Array<ReferenceData>;
    readonly onAssistantPromptAction?: AssistantPromptAction;
    readonly shiftActionPolicy?: ShiftActionPolicy;
}) {
    const {
        outreachStarted,
        showCreateOutreachAction,
        currentOutreachShiftIds,
    } = shiftActionPolicy;
    const { timezone } = usePhoebeChatOrganizationScope();
    // In the extension the card is a self-contained reference — not a link out
    // to the web app — so it renders as a plain container (no navigation).
    const isExtensionSurface = useIsExtensionSurface();
    const shifts = useMemo(() => {
        // Shifts already covered by the current outreach (the one this chat is
        // scoped to) don't get a "Create outreach" affordance — they already
        // have an outreach. Other referenced shifts still can.
        const excludedIds = new Set(
            (currentOutreachShiftIds ?? []).map((id) => id.toLowerCase()),
        );
        return getAssistantResponseShiftReferences(references).filter(
            (shift) => !excludedIds.has(shift.id.toLowerCase()),
        );
    }, [references, currentOutreachShiftIds]);

    if (shifts.length === 0 || outreachStarted) {
        return null;
    }

    // Long shift lists already carry an inline chip per row in the reply;
    // a card per shift would render a wall. Keep only the create action.
    const showCards = shifts.length <= MAX_ASSISTANT_SHIFT_CARDS;

    return (
        <div className="mt-3 grid max-w-md gap-2">
            {/* Same exclusion summary + hover list the recs table shows,
                sitting between the reply and the shift card so the "why
                is the pool thin" answer lives in the conversation flow.
                Renders nothing when there are no exclusions (or tag
                overrides off). */}
            {(showCards ? shifts : []).map((shift) => (
                <div key={`excluded-${shift.id}`} className="[&_.pb-4]:pb-0">
                    <ShiftExcludedCaregiversSummary shiftId={shift.id} />
                </div>
            ))}
            {(showCards ? shifts : []).map((shift) => {
                const startIso = getReferenceMetadataString(
                    shift,
                    'shift_start_time',
                );
                const endIso = getReferenceMetadataString(
                    shift,
                    'shift_end_time',
                );
                const dateLabel = formatAssistantShiftDate(startIso, timezone);
                const timeRangeLabel = formatAssistantShiftTimeRange({
                    startIso,
                    endIso,
                    timezone,
                });
                const statusLabel = getAssistantShiftStatus(shift);

                return (
                    <div
                        key={shift.id}
                        data-testid={`assistant-response-shift-card-${shift.id}`}
                        className={cn(
                            'border-border/60 bg-background rounded-lg border p-2.5 shadow-sm shadow-slate-950/[0.03]',
                            TOOL_SURFACE_HOVER_CLASS,
                        )}
                    >
                        <div className="flex items-start gap-2">
                            <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md border border-blue-100 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/50 dark:text-blue-300">
                                <CalendarClock className="size-3.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                                {(() => {
                                    const cardInner = (
                                        <React.Fragment>
                                            <div className="flex min-w-0 items-start justify-between gap-2">
                                                <p className="text-foreground truncate text-sm font-semibold group-hover/shift:underline group-hover/shift:underline-offset-2">
                                                    {getAssistantShiftDisplayName(
                                                        shift,
                                                    )}
                                                </p>
                                                {statusLabel ? (
                                                    <ToolToken
                                                        tone={getAssistantShiftStatusTone(
                                                            shift,
                                                        )}
                                                    >
                                                        {statusLabel}
                                                    </ToolToken>
                                                ) : null}
                                            </div>
                                            <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                                                {dateLabel ? (
                                                    <span>{dateLabel}</span>
                                                ) : null}
                                                {timeRangeLabel ? (
                                                    <span>
                                                        {timeRangeLabel}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </React.Fragment>
                                    );
                                    return isExtensionSurface ? (
                                        <div className="block min-w-0">
                                            {cardInner}
                                        </div>
                                    ) : (
                                        <Link
                                            to="/shifts/$shift-id"
                                            params={{ 'shift-id': shift.id }}
                                            className="group/shift block min-w-0 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                                        >
                                            {cardInner}
                                        </Link>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                );
            })}
            {onAssistantPromptAction &&
            showCreateOutreachAction &&
            !outreachStarted ? (
                <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                        void onAssistantPromptAction(
                            shifts.length === 1
                                ? 'Create a coverage outreach for this shift'
                                : `Create one coverage outreach covering all ${shifts.length} shifts above`,
                        );
                    }}
                    className="mt-1 gap-1.5 justify-self-start"
                >
                    <Send className="size-3.5" />
                    Create outreach
                </Button>
            ) : null}
        </div>
    );
}

function normalizeAssistantSuggestionName(value: string): string {
    return value
        .replace(/\s+/g, ' ')
        .replace(/[^\p{L}\p{N}\s'-]/gu, '')
        .trim()
        .toLowerCase();
}

function stripAssistantSuggestionNameSyntax(value: string): string {
    const withoutMarkdownLink = value.replace(/^\[([^\]]+)\]\([^)]+\)$/u, '$1');
    return withoutMarkdownLink
        .replace(/\*\*/g, '')
        .replace(/__/g, '')
        .replace(/`/g, '')
        .trim();
}

function parseAssistantSuggestionLine(
    line: string,
): { readonly caregiverName: string; readonly reason: string } | null {
    const trimmedLine = line.replace(/^\s*(?:[-*•]|\d+[.)])\s+/u, '').trim();
    const match = trimmedLine.match(/^(.+?)\s+(?:—|–|-)\s+(.+)$/u);
    if (!match) {
        return null;
    }

    const caregiverName = stripAssistantSuggestionNameSyntax(match[1] ?? '');
    const reason = (match[2] ?? '').trim();
    if (!caregiverName || !reason) {
        return null;
    }

    return { caregiverName, reason };
}

function getReferenceMetadataStringValue(
    reference: ReferenceData,
    key: string,
): string | null {
    const value = reference.metadata?.[key];
    return typeof value === 'string' && value.trim().length > 0
        ? value.trim()
        : null;
}

function findCaregiverReferenceByName(
    references: ReadonlyArray<ReferenceData> | undefined,
    caregiverName: string,
): ReferenceData | null {
    const normalizedName = normalizeAssistantSuggestionName(caregiverName);
    if (!normalizedName) {
        return null;
    }

    for (const reference of references ?? []) {
        if (reference.type !== 'caregiver') {
            continue;
        }
        const candidateNames = [
            reference.display_name,
            getReferenceMetadataStringValue(reference, 'name'),
            getReferenceMetadataStringValue(reference, 'caregiver_name'),
        ].filter((name): name is string => Boolean(name));
        if (
            candidateNames.some(
                (name) =>
                    normalizeAssistantSuggestionName(name) === normalizedName,
            )
        ) {
            return reference;
        }
    }

    return null;
}

function getAssistantSuggestionRoleLabel(
    reason: string,
    reference: ReferenceData | null,
): string | null {
    const metadataRoles = reference?.metadata?.['roles'];
    if (Array.isArray(metadataRoles)) {
        const firstRole = metadataRoles.find(
            (role): role is string =>
                typeof role === 'string' && role.trim().length > 0,
        );
        if (firstRole) {
            return firstRole.toUpperCase();
        }
    }
    if (typeof metadataRoles === 'string' && metadataRoles.trim()) {
        return metadataRoles.trim().toUpperCase();
    }

    const roleMatch = reason.match(/\b(RN|LPN|LVN|CNA|HHA)\b/i);
    return roleMatch ? roleMatch[1]!.toUpperCase() : null;
}

export function parseAssistantCaregiverSuggestionResponse(
    content: string,
    references: ReadonlyArray<ReferenceData> | undefined,
): AssistantCaregiverSuggestionResponse | null {
    const lines = content.split(/\r?\n/u);
    const headingIndex = lines.findIndex((line) =>
        /^top suggestions:?$/iu.test(line.trim()),
    );
    if (headingIndex < 0) {
        return null;
    }

    const suggestions: Array<AssistantCaregiverSuggestion> = [];
    let suggestionBlockEndIndex = headingIndex + 1;
    for (let index = headingIndex + 1; index < lines.length; index += 1) {
        const line = lines[index] ?? '';
        if (!line.trim()) {
            if (suggestions.length > 0) {
                suggestionBlockEndIndex = index + 1;
            }
            continue;
        }

        const parsedLine = parseAssistantSuggestionLine(line);
        if (!parsedLine) {
            if (suggestions.length > 0) {
                suggestionBlockEndIndex = index;
                break;
            }
            continue;
        }

        const reference = findCaregiverReferenceByName(
            references,
            parsedLine.caregiverName,
        );
        suggestions.push({
            rank: suggestions.length + 1,
            caregiverName: parsedLine.caregiverName,
            reason: parsedLine.reason,
            reference,
            roleLabel: getAssistantSuggestionRoleLabel(
                parsedLine.reason,
                reference,
            ),
        });
        suggestionBlockEndIndex = index + 1;
    }

    if (suggestions.length < 2) {
        return null;
    }

    return {
        intro: lines.slice(0, headingIndex).join('\n').trim(),
        outro: lines.slice(suggestionBlockEndIndex).join('\n').trim(),
        suggestions,
    };
}

function AssistantSuggestionCaregiverName({
    suggestion,
    onCaregiverClick,
}: {
    readonly suggestion: AssistantCaregiverSuggestion;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const reference = suggestion.reference;
    if (reference && onCaregiverClick) {
        return (
            <button
                type="button"
                onClick={() => onCaregiverClick(reference.id)}
                className="text-primary decoration-primary/40 hover:decoration-primary cursor-pointer text-left text-sm font-semibold underline underline-offset-2"
            >
                {suggestion.caregiverName}
            </button>
        );
    }

    if (reference) {
        const linkedName = renderReferenceBadgeLink(
            reference,
            'text-primary decoration-primary/40 hover:decoration-primary cursor-pointer text-left text-sm font-semibold underline underline-offset-2',
            suggestion.caregiverName,
        );
        if (linkedName) {
            return linkedName;
        }
    }

    return (
        <span className="text-foreground text-sm font-semibold">
            {suggestion.caregiverName}
        </span>
    );
}

function AssistantCaregiverSuggestionList({
    suggestions,
    onCaregiverClick,
    onAssistantPromptAction,
}: {
    readonly suggestions: Array<AssistantCaregiverSuggestion>;
    readonly onCaregiverClick?: (caregiverId: string) => void;
    readonly onAssistantPromptAction?: AssistantPromptAction;
}) {
    const caregiverNames = suggestions
        .map((suggestion) => suggestion.caregiverName)
        .join(', ');
    const promptActions = onAssistantPromptAction
        ? [
              {
                  label: 'Adjust list',
                  prompt: 'Adjust this caregiver suggestion list and explain the changes.',
              },
              {
                  label: 'Add caregivers',
                  prompt: 'Add more caregiver suggestions for this shift, keeping the current list in mind.',
              },
              {
                  label: 'Start outreach',
                  prompt: `Start outreach with these suggested caregivers: ${caregiverNames}.`,
              },
          ]
        : [];

    return (
        <div
            data-testid="assistant-caregiver-suggestion-list"
            className="border-border/60 bg-background mt-3 max-w-2xl rounded-lg border p-3 shadow-sm shadow-slate-950/[0.03]"
        >
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <p className="text-foreground text-sm font-semibold">
                        Top caregiver suggestions
                    </p>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                        Ranked for this shift
                    </p>
                </div>
                <Badge variant="secondary">
                    {suggestions.length} suggestions
                </Badge>
            </div>
            <div className="mt-3 divide-y">
                {suggestions.map((suggestion) => (
                    <div
                        key={`${suggestion.rank}-${suggestion.caregiverName}`}
                        className="flex gap-3 py-2.5 first:pt-0 last:pb-0"
                    >
                        <div className="bg-muted text-muted-foreground flex h-6 min-w-6 items-center justify-center rounded-md text-xs font-semibold">
                            {suggestion.rank}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                                <AssistantSuggestionCaregiverName
                                    suggestion={suggestion}
                                    onCaregiverClick={onCaregiverClick}
                                />
                                {suggestion.roleLabel ? (
                                    <ToolToken tone="blue">
                                        {suggestion.roleLabel}
                                    </ToolToken>
                                ) : null}
                            </div>
                            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                                {suggestion.reason}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
            {promptActions.length > 0 ? (
                <div className="border-border/60 mt-3 flex flex-wrap gap-2 border-t pt-3">
                    {promptActions.map((action) => (
                        <Button
                            key={action.label}
                            type="button"
                            size="sm"
                            variant={
                                action.label === 'Start outreach'
                                    ? 'default'
                                    : 'outline'
                            }
                            className="h-7 px-2 text-xs"
                            onClick={() => {
                                void onAssistantPromptAction?.(action.prompt);
                            }}
                        >
                            {action.label}
                        </Button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

// react-markdown renders each table element through a custom component, so
// `element.type` is a function (not the string "tr"). It does pass the source
// hast node, whose `tagName` is the reliable "tr"/"th"/"td" signal.
type MarkdownElement = ReactElement<{
    readonly children?: ReactNode;
    readonly node?: { readonly tagName?: string };
}>;

function markdownTagName(element: MarkdownElement): string | undefined {
    return element.props.node?.tagName;
}

function tableRowElements(node: ReactNode): Array<MarkdownElement> {
    const rows: Array<MarkdownElement> = [];
    React.Children.forEach(node, (child) => {
        if (!React.isValidElement<MarkdownElement['props']>(child)) {
            return;
        }
        if (markdownTagName(child) === 'tr') {
            rows.push(child);
        } else {
            rows.push(...tableRowElements(child.props.children));
        }
    });
    return rows;
}

function tableRowCells(row: MarkdownElement): Array<MarkdownElement> {
    return React.Children.toArray(row.props.children).filter(
        (cell): cell is MarkdownElement =>
            React.isValidElement(cell) &&
            (markdownTagName(cell as MarkdownElement) === 'th' ||
                markdownTagName(cell as MarkdownElement) === 'td'),
    );
}

// Render a markdown table as stacked cards (one card per body row, each column
// a labeled line) for narrow surfaces where a multi-column table cannot fit and
// horizontal scrolling is a poor experience. Mirrors how Slack renders tabular
// agent output as cards. Falls back to a plain table if the structure is not a
// standard header + body table.
type ShiftColumnRole =
    | 'client'
    | 'caregiver'
    | 'start'
    | 'end'
    | 'time'
    | 'date'
    | 'city'
    | 'office'
    | 'status'
    | 'requirements';

const SHIFT_COLUMN_MATCHERS: ReadonlyArray<{
    readonly role: ShiftColumnRole;
    readonly pattern: RegExp;
}> = [
    { role: 'client', pattern: /client|patient|consumer/i },
    { role: 'caregiver', pattern: /caregiver|aide|worker|employee/i },
    { role: 'start', pattern: /start/i },
    { role: 'end', pattern: /\bend\b|finish/i },
    { role: 'time', pattern: /^time$|hours/i },
    { role: 'date', pattern: /date|\bday\b/i },
    { role: 'city', pattern: /city|town/i },
    { role: 'office', pattern: /office|branch|region/i },
    { role: 'status', pattern: /status|state/i },
    { role: 'requirements', pattern: /require|skill|\brole\b/i },
];

function extractNodeText(node: ReactNode): string {
    if (node === null || node === undefined || typeof node === 'boolean') {
        return '';
    }
    if (typeof node === 'string' || typeof node === 'number') {
        return String(node);
    }
    if (Array.isArray(node)) {
        return node.map(extractNodeText).join('');
    }
    if (React.isValidElement<{ readonly children?: ReactNode }>(node)) {
        return extractNodeText(node.props.children);
    }
    return '';
}

function buildShiftColumnIndex(
    headerTexts: ReadonlyArray<string>,
): Partial<Record<ShiftColumnRole, number>> {
    const index: Partial<Record<ShiftColumnRole, number>> = {};
    headerTexts.forEach((headerText, columnIndex) => {
        for (const { role, pattern } of SHIFT_COLUMN_MATCHERS) {
            if (index[role] === undefined && pattern.test(headerText)) {
                index[role] = columnIndex;
                break;
            }
        }
    });
    return index;
}

function isShiftShapedTable(
    index: Partial<Record<ShiftColumnRole, number>>,
): boolean {
    const hasEntity =
        index.client !== undefined || index.caregiver !== undefined;
    const hasWhen =
        index.start !== undefined ||
        index.time !== undefined ||
        index.date !== undefined;
    return hasEntity && hasWhen;
}

function isBlankCellText(text: string): boolean {
    const trimmed = text.trim();
    return trimmed === '' || /^[—–-]+$/.test(trimmed);
}

// Compact shift card for shift-shaped tables, mirroring the search-tool result
// card (card frame + status token + muted date/time/city subline + a labeled
// "Assignment" section). Entity cells keep whatever the table rendered, so
// caregiver/client badges stay clickable and open the in-panel detail modal.
function SmartShiftCards({
    bodyRows,
    columnIndex,
}: {
    readonly bodyRows: ReadonlyArray<MarkdownElement>;
    readonly columnIndex: Partial<Record<ShiftColumnRole, number>>;
}) {
    return (
        <div className="my-2 flex flex-col gap-2">
            {bodyRows.map((row, rowIndex) => {
                const cells = tableRowCells(row);
                const cellNode = (role: ShiftColumnRole): ReactNode => {
                    const columnIdx = columnIndex[role];
                    if (columnIdx === undefined) {
                        return null;
                    }
                    const cell = cells[columnIdx];
                    if (cell === undefined) {
                        return null;
                    }
                    return isBlankCellText(extractNodeText(cell.props.children))
                        ? null
                        : cell.props.children;
                };
                const client = cellNode('client');
                const caregiver = cellNode('caregiver');
                const heading = client ?? caregiver;
                const assignedCaregiver = client !== null ? caregiver : null;
                const start = cellNode('start');
                const end = cellNode('end');
                const time =
                    start !== null && end !== null ? (
                        <React.Fragment>
                            {start} - {end}
                        </React.Fragment>
                    ) : (
                        cellNode('time')
                    );
                const date = cellNode('date');
                const city = cellNode('city');
                const office = cellNode('office');
                const requirements = cellNode('requirements');
                const subline = [date, time, city, office].filter(
                    (part) => part !== null,
                );
                // Only an EXPLICIT status column renders a pill. Deriving
                // Open/Assigned from caregiver presence fabricated statuses
                // on non-shift tables (rosters, billing rows) that happen to
                // match the shift-shaped heuristic.
                const explicitStatus = cellNode('status');
                const statusToken =
                    explicitStatus !== null ? (
                        <ToolToken>{explicitStatus}</ToolToken>
                    ) : null;
                return (
                    <div
                        key={rowIndex}
                        className={cn(
                            'border-border/60 bg-card rounded-lg border p-3',
                            TOOL_SURFACE_HOVER_CLASS,
                        )}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="text-foreground min-w-0 text-sm font-semibold break-words">
                                    {heading}
                                </div>
                                {subline.length > 0 ? (
                                    <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px]">
                                        {subline.map((part, partIndex) => (
                                            <span key={partIndex}>{part}</span>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                            {statusToken !== null ? (
                                <div className="shrink-0">{statusToken}</div>
                            ) : null}
                        </div>
                        {assignedCaregiver !== null ? (
                            <div className="mt-3">
                                <ToolTopicSection title="Assignment">
                                    {assignedCaregiver}
                                </ToolTopicSection>
                            </div>
                        ) : null}
                        {requirements !== null ? (
                            <div className="mt-2">
                                <ToolTopicSection title="Requirements">
                                    {requirements}
                                </ToolTopicSection>
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

function StackedTableCards({ children }: { readonly children?: ReactNode }) {
    const [headerRow, ...bodyRows] = tableRowElements(children);
    if (headerRow === undefined || bodyRows.length === 0) {
        return <table>{children}</table>;
    }
    const headerCells = tableRowCells(headerRow);
    const columnIndex = buildShiftColumnIndex(
        headerCells.map((cell) => extractNodeText(cell.props.children)),
    );
    if (isShiftShapedTable(columnIndex)) {
        return (
            <SmartShiftCards bodyRows={bodyRows} columnIndex={columnIndex} />
        );
    }
    const headerLabels = headerCells.map((cell) => cell.props.children);
    return (
        <div className="my-2 flex flex-col gap-2">
            {bodyRows.map((row, rowIndex) => (
                <div
                    key={rowIndex}
                    className="border-border bg-muted/40 flex min-w-0 flex-col gap-1 rounded-lg border p-2.5"
                >
                    {tableRowCells(row).map((cell, cellIndex) => (
                        <div
                            key={cellIndex}
                            className="flex min-w-0 gap-2 text-sm leading-snug"
                        >
                            {headerLabels[cellIndex] ? (
                                <span className="text-muted-foreground min-w-16 shrink-0 font-medium">
                                    {headerLabels[cellIndex]}
                                </span>
                            ) : null}
                            <span className="min-w-0 flex-1 break-words">
                                {cell.props.children}
                            </span>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

export function AssistantResponseContent({
    content,
    destinationLinks,
    references,
    slackUserMentions,
    onCaregiverClick,
    onAssistantPromptAction,
    shiftSuggestionTabs,
    shiftActionPolicy = DEFAULT_SHIFT_ACTION_POLICY,
    openLinksInNewTab = false,
}: {
    readonly content: string;
    readonly destinationLinks?: Array<MessageDestinationLink>;
    readonly references?: Array<ReferenceData>;
    readonly slackUserMentions?: Array<SlackUserMentionData>;
    readonly onCaregiverClick?: (caregiverId: string) => void;
    readonly onAssistantPromptAction?: AssistantPromptAction;
    readonly shiftSuggestionTabs?: {
        readonly tabs: Array<ShiftTab>;
        readonly turnSequence: number;
    } | null;
    readonly shiftActionPolicy?: ShiftActionPolicy;
    readonly openLinksInNewTab?: boolean;
}) {
    const isExtensionSurface = useIsExtensionSurface();
    const hasMultiShiftTabs =
        shiftSuggestionTabs !== null &&
        shiftSuggestionTabs !== undefined &&
        shiftSuggestionTabs.tabs.length > 0;
    const refMap = useMemo(
        () => new Map((references ?? []).map((r) => [r.id, r])),
        [references],
    );
    const slackMentionMap = useMemo(
        () => buildSlackMentionMap(slackUserMentions),
        [slackUserMentions],
    );
    const suggestionResponse = useMemo(
        () => parseAssistantCaregiverSuggestionResponse(content, references),
        [content, references],
    );

    const renderMarkdown = (markdownContent: string) => {
        if (!markdownContent.trim()) {
            return null;
        }
        const processedContent = processContentForMarkdown(
            markdownContent,
            refMap,
            slackMentionMap,
        );
        return (
            <Markdown
                className="max-w-full min-w-0 overflow-x-hidden"
                components={{
                    ...(hasMultiShiftTabs
                        ? { table: () => null }
                        : isExtensionSurface
                          ? {
                                table: ({ children }) => (
                                    <StackedTableCards>
                                        {children}
                                    </StackedTableCards>
                                ),
                            }
                          : {}),
                    a: ({ href, children }) =>
                        renderReferenceAwareAnchor(href, children, refMap, {
                            openInNewTab: openLinksInNewTab,
                        }),
                }}
            >
                {processedContent}
            </Markdown>
        );
    };
    const suggestionShiftView = hasMultiShiftTabs ? (
        <SuggestionsShiftView
            shiftTabs={shiftSuggestionTabs.tabs}
            onCaregiverClick={onCaregiverClick}
        />
    ) : null;

    if (suggestionResponse) {
        return (
            <React.Fragment>
                {suggestionShiftView}
                {renderMarkdown(suggestionResponse.intro)}
                <AssistantCaregiverSuggestionList
                    suggestions={suggestionResponse.suggestions}
                    onCaregiverClick={onCaregiverClick}
                    onAssistantPromptAction={onAssistantPromptAction}
                />
                {renderMarkdown(suggestionResponse.outro)}
                <AssistantResponseShiftCards
                    references={references}
                    onAssistantPromptAction={onAssistantPromptAction}
                    shiftActionPolicy={shiftActionPolicy}
                />
                <AssistantResponseCalloutLinks references={references} />
                <AssistantResponseDestinationLinks
                    destinationLinks={destinationLinks}
                />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {suggestionShiftView}
            {renderMarkdown(content)}
            <AssistantResponseShiftCards
                references={references}
                onAssistantPromptAction={onAssistantPromptAction}
                shiftActionPolicy={shiftActionPolicy}
            />
            <AssistantResponseCalloutLinks references={references} />
            <AssistantResponseDestinationLinks
                destinationLinks={destinationLinks}
            />
        </React.Fragment>
    );
}

function SuggestionsShiftView({
    shiftTabs,
    onCaregiverClick,
}: {
    readonly shiftTabs: Array<ShiftTab>;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    const [openShiftIds, setOpenShiftIds] = useState<Set<string>>(
        () => new Set(),
    );
    const accordionId = useId();

    useEffect(() => {
        setOpenShiftIds((currentOpenShiftIds) => {
            const availableShiftIds = new Set(
                shiftTabs.map((tab) => tab.shiftId),
            );
            const nextOpenShiftIds = new Set(
                [...currentOpenShiftIds].filter((shiftId) =>
                    availableShiftIds.has(shiftId),
                ),
            );
            if (
                nextOpenShiftIds.size === currentOpenShiftIds.size &&
                [...nextOpenShiftIds].every((shiftId) =>
                    currentOpenShiftIds.has(shiftId),
                )
            ) {
                return currentOpenShiftIds;
            }
            return nextOpenShiftIds;
        });
    }, [shiftTabs]);

    const toggleShift = (shiftId: string) => {
        setOpenShiftIds((currentOpenShiftIds) => {
            const nextOpenShiftIds = new Set(currentOpenShiftIds);
            if (nextOpenShiftIds.has(shiftId)) {
                nextOpenShiftIds.delete(shiftId);
            } else {
                nextOpenShiftIds.add(shiftId);
            }
            return nextOpenShiftIds;
        });
    };

    return (
        <div
            role="region"
            aria-label="Recommended caregiver suggestions"
            className="border-border/60 bg-card mt-3 w-0 max-w-full min-w-full space-y-2 overflow-hidden rounded-lg border p-2"
        >
            <div
                role="group"
                aria-label="Recommended caregiver groups and shifts"
                className="w-0 max-w-full min-w-full space-y-2"
            >
                {shiftTabs.map((tab, index) => {
                    const isOpen = openShiftIds.has(tab.shiftId);
                    const panelId = `${accordionId}-recommended-caregivers-${index}`;
                    const headerGroups = Array.from(
                        tab.headerRows.reduce((groups, row) => {
                            const separator = ' - ';
                            const separatorIndex = row.indexOf(separator);
                            const clientName =
                                separatorIndex >= 0
                                    ? row.slice(0, separatorIndex)
                                    : row;
                            const timeRow =
                                separatorIndex >= 0
                                    ? row.slice(
                                          separatorIndex + separator.length,
                                      )
                                    : null;
                            const timeRows = groups.get(clientName) ?? [];
                            if (timeRow) {
                                timeRows.push({
                                    label: timeRow,
                                    selectedCount: tab.suggestions.length,
                                });
                            }
                            groups.set(clientName, timeRows);
                            return groups;
                        }, new Map<string, Array<SuggestionAccordionTimeRow>>()),
                    ).map(([clientName, timeRows]) => ({
                        clientName,
                        timeRows,
                    }));
                    return (
                        <div
                            key={tab.shiftId}
                            className="border-border/70 bg-background overflow-hidden rounded-md border"
                        >
                            <button
                                type="button"
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                title={tab.label}
                                onClick={() => toggleShift(tab.shiftId)}
                                className="hover:bg-muted/60 flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left transition-colors"
                            >
                                <span className="min-w-0 space-y-1">
                                    {headerGroups.map(
                                        ({ clientName, timeRows }) => (
                                            <span
                                                key={clientName}
                                                className="block space-y-0.5"
                                            >
                                                <span className="text-foreground block text-sm leading-snug font-medium">
                                                    {clientName}
                                                </span>
                                                {timeRows.length === 0 ? (
                                                    <span className="text-muted-foreground block text-sm leading-snug font-medium">
                                                        {tab.suggestions.length}{' '}
                                                        selected
                                                    </span>
                                                ) : null}
                                                {timeRows.map((timeRow) => (
                                                    <span
                                                        key={`${clientName}-${timeRow.label}`}
                                                        className="text-muted-foreground block text-sm leading-snug"
                                                    >
                                                        {timeRow.label}
                                                        <span className="ml-1 font-medium text-[#5533EE]">
                                                            ·{' '}
                                                            {
                                                                timeRow.selectedCount
                                                            }{' '}
                                                            suggested
                                                        </span>
                                                    </span>
                                                ))}
                                            </span>
                                        ),
                                    )}
                                    {headerGroups.length === 0 ? (
                                        <span className="text-foreground block text-sm leading-snug font-medium">
                                            {tab.label}
                                        </span>
                                    ) : null}
                                </span>
                                <ChevronDown
                                    className={cn(
                                        'text-muted-foreground mt-0.5 size-4 shrink-0 transition-transform',
                                        isOpen ? 'rotate-180' : '',
                                    )}
                                    aria-hidden="true"
                                />
                            </button>
                            {isOpen ? (
                                <div
                                    id={panelId}
                                    className="border-t px-2 py-2"
                                >
                                    <RecommendedCaregiverTable
                                        suggestions={tab.suggestions}
                                        onCaregiverClick={onCaregiverClick}
                                    />
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function RecommendedCaregiverTable({
    suggestions,
    onCaregiverClick,
}: {
    readonly suggestions: Array<ShiftTab['suggestions'][number]>;
    readonly onCaregiverClick?: (caregiverId: string) => void;
}) {
    return (
        <div
            aria-label="Recommended caregivers"
            className="max-h-96 w-0 min-w-full overflow-auto rounded-md border"
        >
            <table className="w-full border-collapse text-sm">
                <thead className="bg-muted sticky top-0 z-10">
                    <tr className="border-border border-b">
                        <th className="px-3 py-2 text-left text-xs font-medium">
                            Caregiver
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium">
                            Reasoning
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {suggestions.map((suggestion) => (
                        <tr
                            key={suggestion.caregiverId}
                            className="border-border border-b"
                        >
                            <td className="px-3 py-2.5 align-top text-sm leading-relaxed">
                                {onCaregiverClick ? (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            onCaregiverClick(
                                                suggestion.caregiverId,
                                            )
                                        }
                                        className="text-primary decoration-primary/40 hover:decoration-primary cursor-pointer text-left underline underline-offset-2"
                                    >
                                        {suggestion.caregiverName}
                                    </button>
                                ) : (
                                    suggestion.caregiverName
                                )}
                            </td>
                            <td className="px-3 py-2.5 align-top text-sm leading-relaxed">
                                <Markdown className="text-sm">
                                    {suggestion.reasoning}
                                </Markdown>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
